// ABOUTME: Renders the favorite star on model-selection product cards, toggling the model's bookmark.
// ABOUTME: Reads the state marked on the catalog payload, so no per-card IsFavorite request is issued.

import type { IProduct } from '@coolshop/cool-model-selection';
import { Box, IconButton, StarBorderIcon, StarIcon } from '@ds/coolshop';
import { useFavoritesEnabled, useFavoriteToggle } from '@src/_new/modules/user-favorites';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useContext } from 'react';
import { useIntl } from 'react-intl';

import { resolveProductFavoriteTarget } from '../../utils/resolveProductFavoriteTarget';
import { productFavoriteKey, useCatalogFavorites } from './CatalogFavoritesContext';

// `isFavorite` is resolved server-side and carried on the catalog payload; the package type does
// not know about it, like the other host-added product fields.
type ProductWithFavorite = IProduct & {
    isFavorite?: boolean;
    isConfigurable?: boolean;
};

/**
 * Start-action slot for the model-selection product card. Lets the user bookmark a model straight
 * from the catalog, before entering the configurator. Renders nothing when favorites are disabled
 * or the card does not resolve to a hierarchy node.
 */
export function ProductFavoriteAction(product: IProduct) {
    const { formatMessage } = useIntl();
    const settings = useContext(SettingsContext);
    const favoritesEnabled = useFavoritesEnabled();
    // Present in the treeview catalog only, whose favorites strip follows the stars.
    const catalogFavorites = useCatalogFavorites();

    const { id, isFavorite: markedFavorite, isConfigurable } = product as ProductWithFavorite;
    const target = resolveProductFavoriteTarget(
        { id, isConfigurable },
        {
            model: settings.prototype?.hierarchies?.model,
            prototype: settings.prototype?.hierarchies?.prototype,
        },
    );

    const { isFavorite, toggling, toggleWithFeedback } = useFavoriteToggle({
        hierarchyId: target?.hierarchyId ?? '',
        nodeId: target?.nodeId ?? '',
        skip: !favoritesEnabled || !target,
        knownIsFavorite: markedFavorite === true,
    });

    if (!favoritesEnabled || !target) {
        return null;
    }

    // The card itself is clickable (it opens the configurator), so the star's clicks must not
    // bubble up to it.
    const stopAndRun = (run: () => void) => (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        run();
    };

    return (
        <Box onClick={stopAndRun(() => undefined)}>
            <IconButton
                size="small"
                aria-label={
                    isFavorite
                        ? formatMessage({
                              id: 'catalog.favorite.remove',
                              defaultMessage: 'Remove from favorites',
                          })
                        : formatMessage({
                              id: 'catalog.favorite.add',
                              defaultMessage: 'Add to favorites',
                          })
                }
                data-testid={`product-favorite-button-${id}`}
                disabled={toggling}
                onClick={stopAndRun(() => {
                    toggleWithFeedback().then(result => {
                        if (result) {
                            catalogFavorites?.setFavorite(
                                productFavoriteKey(id),
                                result.isFavorite,
                            );
                        }
                    });
                })}
            >
                {isFavorite ? <StarIcon color="warning" /> : <StarBorderIcon />}
            </IconButton>
        </Box>
    );
}
